
import "./styles/posting-table.scss";
import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import { mapState } from "vuex";
import ConfirmedCard from "./cards/confirmed-card.vue";
import OpenCard from "./cards/open-card.vue";
import RequestedCard from "./cards/requested-card.vue";
import GradingCard from "./cards/grading-card.vue";
import AuthMixin from "@/common/mixins/auth.mixin";
import { RatingDto, RatingFormDto } from "@/types/rating.type";
import ShiftDeleteModal from "./modals/shift-delete-modal.vue";
import ShiftMixin from "./shift.mixin";
import { AcceptedShift, OpenShift, RequestedShift, Shift } from "./shift.types";
import { OfficeDto } from "@/types/office.type";
import { ShiftDataDto } from "@/types/shift.type";
import { RatingStatus, ResponseStatus, SubscriptionStatus } from "@/common/constants";
import { ShiftGroupResponseStatusFormDto } from "@/types/shift-response.type";

@Component({
  components: { 
    OpenCard,
    RequestedCard,
    ConfirmedCard,
    GradingCard,
    ShiftDeleteModal
  },
  computed: {
    ...mapState("Shift", 
    [
      "selectedOffice", 
      "openShifts", 
      "requestedShifts",
      "confirmedShifts",
      "openRatings",
      "shiftData"
    ])
  }
})
export default class ShiftKanban extends Mixins(AuthMixin, ShiftMixin) {
  @Prop() currentUserFilter!: boolean;

  openShifts!: OpenShift[];
  requestedShifts!: RequestedShift[];
  confirmedShifts!: AcceptedShift[];
  openRatings!: RatingDto[];
  selectedOffice!: OfficeDto;
  shiftData!: ShiftDataDto | null;

  selectedShift: Shift | null = null;

  async mounted(): Promise<void> {
    this.setLoading(true);
    
    this.timerRef = setInterval(() => this.getShifts(true, this.selectedOffice.id), 60000);

    this.setLoading(false);
  }

  destroyed(): void {
    clearInterval(this.timerRef);
  }

  @Watch('selectedOffice')
  async onSelectedOfficeChange(): Promise<void> {
    this.setLoading(true);

    await this.getShifts(true, this.selectedOffice.id);

    this.setLoading(false);
  }

  async onDelete(): Promise<void> {
    this.setLoading(true);
    
    await this.getShifts(true, this.selectedOffice.id);
    
    this.setLoading(false);
  }

  async onDeleteClick(shift: Shift): Promise<void> {
    this.selectedShift = shift;

    this.$bvModal.show("shift-delete-modal");
  }

  async confirmShift(userId: number, shiftResponseId: number): Promise<void> {
    if (!this.shiftData) {
      return;
    }
    
    const subscriptionActiveStatus = SubscriptionStatus.Active.getId(this.shiftData.subscriptionStatuses);
    const subscriptionTrialingStatus = SubscriptionStatus.Trialing.getId(this.shiftData.subscriptionStatuses);

    if (this.selectedOffice?.subscriptionStatus !== subscriptionActiveStatus && this.selectedOffice?.subscriptionStatus !== subscriptionTrialingStatus) {
      this.$emit("notSubscribed");

      return;
    }

    this.setLoading(true);

    await this.$store.dispatch("Shift/updateShiftResponseStatus", {
      id: shiftResponseId,
      userId,
      status: ResponseStatus.Confirmed.getId(this.shiftData.responseStatuses)
    } as ShiftGroupResponseStatusFormDto);

    await this.getShifts(true, this.selectedOffice.id);

    await this.$store.dispatch("Shift/getOffices", { userId: this.userInfo()?.sub, officeId: this.selectedOffice.id });

    this.setLoading(false);
  }

  getShiftList(jobPosts: Shift[]): Shift[] {
    if (!this.currentUserFilter){
      return jobPosts;
    }

    return jobPosts.filter(jobPost => jobPost.postedByUserId === this.userInfo()?.sub);
  }

  async saveRating(form: RatingFormDto): Promise<void> {
    if (!this.shiftData) {
      return;
    }

    this.setLoading(true);

    await this.$store.dispatch("Ratings/updateRating", {
      id: form.id,
      rating: form.rating,
      comments: form.comments,
      status: RatingStatus.Active.getId(this.shiftData.ratingStatuses),
      raterUserId: this.userInfo()?.sub,
      noShow: form.noShow,
      sickCall: form.sickCall
    } as RatingFormDto);
    
    await this.getShifts(true, this.selectedOffice.id);

    this.setLoading(false);
  }

  async dismissRating(form: RatingFormDto): Promise<void> {
    if (!this.shiftData) {
      return;
    }
    
    this.setLoading(true);

    await this.$store.dispatch("Ratings/updateRating", {
      id: form.id,
      rating: 0,
      comments: null,
      status: RatingStatus.Dismissed.getId(this.shiftData.ratingStatuses),
      raterUserId: this.userInfo()?.sub
    } as RatingFormDto);

    await this.getShifts(true, this.selectedOffice.id);

    this.setLoading(false);
  }
}
